import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  isJobTreadConnected,
  getJobTreadOrganizations,
  getJobTreadCustomerOrders,
  getJobTreadReceivables,
  getJobTreadPayables,
  resolveJobTreadOrg
} from '../services/jobtreadService';
import { loadOverheadData, quickOverheadAnnual } from '../services/overheadService';
import { getMetricStatus, getCoachingCue } from '../components/finance/financeBenchmarks';
import { makeTrace, fmtN } from '../utils/metricTrace';

/* ------------------------------- helpers -------------------------------- */

const fmtCurrency = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);

const fmtPercent = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? '—' : `${(Math.round(v * Math.pow(10, digits)) / Math.pow(10, digits)).toFixed(digits)}%`;

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Timeframe options for the finance analytics dashboard. */
export const FINANCE_TIMEFRAMES = [
  { id: 'ytd', label: 'This Year' },
  { id: 'last3', label: 'Last 3 Months' },
  { id: 'last6', label: 'Last 6 Months' },
  { id: 'last12', label: 'Last 12 Months' },
  { id: 'lastYear', label: 'Last Year' },
];

export const financeTimeframeLabel = (id) =>
  FINANCE_TIMEFRAMES.find((t) => t.id === id)?.label || 'This Year';

/**
 * Month buckets + date bounds for a timeframe. Mirrors the sales dashboard so
 * both analytics tabs scope data the same way.
 */
const buildTimeframeRange = (timeframe, today) => {
  const y = today.getFullYear();
  const months = [];
  const push = (yy, mm) =>
    months.push({ year: yy, monthIndex: mm, label: `${MONTH_SHORT[mm]}${yy !== y ? ` '${String(yy).slice(2)}` : ''}` });
  if (timeframe === 'lastYear') {
    for (let m = 0; m < 12; m++) push(y - 1, m);
  } else if (timeframe === 'last3' || timeframe === 'last6' || timeframe === 'last12') {
    const n = timeframe === 'last3' ? 3 : timeframe === 'last6' ? 6 : 12;
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(y, today.getMonth() - i, 1);
      push(d.getFullYear(), d.getMonth());
    }
  } else {
    for (let m = 0; m < 12; m++) push(y, m);
  }
  const start = new Date(months[0].year, months[0].monthIndex, 1);
  const end = new Date(months[months.length - 1].year, months[months.length - 1].monthIndex + 1, 1);
  return { months, start, end };
};

/**
 * Resolve the company's overhead to monthly + annual dollars. Honors the two
 * Overhead Calculator modes: 'quick' (one fixed total typed in) and 'detailed'
 * (itemized expense tables, annualized with the calculator's own frequency
 * math). Returns { mode } so callers can label the source.
 */
const computeMonthlyOverhead = (overheadData) => {
  if (!overheadData) return { monthlyOverhead: null, annualOverhead: null, itemCount: 0, mode: null };

  // Quick mode: a single fixed amount stands in for all overhead.
  if (overheadData.inputMode === 'quick') {
    const annual = quickOverheadAnnual(overheadData.quickOverhead);
    if (annual <= 0) return { monthlyOverhead: null, annualOverhead: null, itemCount: 0, mode: 'quick' };
    return { monthlyOverhead: annual / 12, annualOverhead: annual, itemCount: 1, mode: 'quick' };
  }

  // Detailed mode: sum the itemized expense tables.
  const expenses = overheadData.expenses;
  if (!expenses) return { monthlyOverhead: null, annualOverhead: null, itemCount: 0, mode: 'detailed' };
  let annual = 0;
  let itemCount = 0;
  Object.values(expenses).forEach((items) => {
    (items || []).forEach((item) => {
      const amount = parseFloat(item?.amount);
      if (!Number.isFinite(amount) || amount <= 0) return;
      const f = item?.frequency;
      annual += f === 'Weekly' ? amount * 52 : f === 'Monthly' ? amount * 12 : f === 'Quarterly' ? amount * 4 : amount;
      itemCount += 1;
    });
  });
  if (itemCount === 0 || annual <= 0) return { monthlyOverhead: null, annualOverhead: null, itemCount, mode: 'detailed' };
  return { monthlyOverhead: annual / 12, annualOverhead: annual, itemCount, mode: 'detailed' };
};

const EMPTY_METRIC = { value: '—', rawValue: null, status: 'none', source: 'none', series: [], sub: null, cue: null, trace: null };

// Same status semantics as the sales dashboard so both tabs agree on what
// counts as signed work.
const isApprovedOrder = (o) => o.status.includes('approved') || o.status.includes('signed');

/**
 * Finance analytics dashboard hook — powers the portal Finance ▸ Analytics tab.
 *
 * JobTread-sourced (QuickBooks is parked as "coming soon"): margins and gross
 * profit come from approved customer orders (price vs estimated cost);
 * receivables/payables from pending customer invoices and vendor bills.
 * Net margin = gross profit minus the client's Overhead Calculator overhead —
 * the one deliberate manual input on this tab, because overhead lives nowhere
 * in JobTread.
 */
export const useFinanceAnalytics = (timeframe = 'ytd', { userId = null } = {}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reloadIndex, setReloadIndex] = useState(0);
  const refresh = () => setReloadIndex((i) => i + 1);
  const [state, setState] = useState({
    jobtreadConnected: false,
    jobtreadError: null,
    asOfDate: new Date(),
    debug: null,
    metrics: {
      gpm: EMPTY_METRIC,
      grossProfit: EMPTY_METRIC,
      netMargin: EMPTY_METRIC,
      netProfit: EMPTY_METRIC,
      receivables: EMPTY_METRIC,
      payables: EMPTY_METRIC,
      overhead: EMPTY_METRIC
    }
  });

  useEffect(() => {
    const uid = userId || currentUser?.uid;
    if (!uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      const today = new Date();
      const tf = buildTimeframeRange(timeframe, today);
      const tfLabel = financeTimeframeLabel(timeframe);
      const monthPos = new Map(tf.months.map((mo, i) => [`${mo.year}-${mo.monthIndex}`, i]));
      const fetches = [];

      // JobTread grants live server-side; queries pass targetUserId and the
      // server resolves that user's grant (the caller's own when uid is theirs).
      const jobtreadConnected = await isJobTreadConnected(uid).catch(() => false);

      /* -------- JobTread + overhead inputs (parallel, defensive) -------- */
      // Every JobTread query runs under the target user's grant — passed
      // explicitly per call. This hook previously never passed it, so in CRM
      // Portal View the queries fell back to the logged-in viewer's own
      // (usually missing) grant and Finance Analytics always came up empty.
      let orders = null;
      let ordersDiagnostics = null;
      let receivables = null;
      let payables = null;
      let jtOrg = { orgId: null, orgName: null, orgCount: 0, multipleOrgs: false };
      let jtError = null;
      const overheadPromise = loadOverheadData(uid).catch(() => null);
      if (jobtreadConnected) {
        try {
          const orgs = await getJobTreadOrganizations({ targetUserId: uid });
          jtOrg = resolveJobTreadOrg(orgs);
          if (!jtOrg.orgId) {
            jtError = 'The JobTread grant key works but belongs to no organization.';
          }
        } catch (e) {
          jtError = e?.message || String(e);
          fetches.push({ key: 'jt.orgs', fn: 'getJobTreadOrganizations', params: {}, status: 'rejected', error: jtError });
        }
        if (jtOrg.orgId) {
          const [ordersRes, receivablesRes, payablesRes] = await Promise.allSettled([
            getJobTreadCustomerOrders(jtOrg.orgId, { targetUserId: uid }),
            getJobTreadReceivables(jtOrg.orgId, { targetUserId: uid }),
            getJobTreadPayables(jtOrg.orgId, { targetUserId: uid })
          ]);
          if (ordersRes.status === 'fulfilled') {
            orders = ordersRes.value?.orders || [];
            ordersDiagnostics = ordersRes.value?.diagnostics || null;
          }
          receivables = receivablesRes.status === 'fulfilled' ? receivablesRes.value : null;
          payables = payablesRes.status === 'fulfilled' ? payablesRes.value : null;
          fetches.push(
            { key: 'jt.orders', fn: 'getJobTreadCustomerOrders', params: { orgId: jtOrg.orgId }, status: ordersRes.status, error: ordersRes.status === 'rejected' ? String(ordersRes.reason?.message || ordersRes.reason) : null },
            { key: 'jt.receivables', fn: 'getJobTreadReceivables', params: { orgId: jtOrg.orgId }, status: receivablesRes.status, error: receivablesRes.status === 'rejected' ? String(receivablesRes.reason?.message || receivablesRes.reason) : null },
            { key: 'jt.payables', fn: 'getJobTreadPayables', params: { orgId: jtOrg.orgId }, status: payablesRes.status, error: payablesRes.status === 'rejected' ? String(payablesRes.reason?.message || payablesRes.reason) : null }
          );
        }
        // Surface failures the per-fetch wrappers swallow into diagnostics —
        // otherwise a dead grant (e.g. expired JobTread subscription) renders
        // as an indistinguishable "no data" empty state.
        if (!jtError) {
          jtError =
            ordersDiagnostics?.error ||
            receivables?.diagnostics?.error ||
            payables?.diagnostics?.error ||
            fetches.find((f) => f.key.startsWith('jt.') && f.status === 'rejected')?.error ||
            null;
        }
      }
      const overheadData = await overheadPromise;
      const { monthlyOverhead, annualOverhead, itemCount: overheadItemCount, mode: overheadMode } = computeMonthlyOverhead(overheadData);
      fetches.push({ key: 'manual.overhead', fn: 'loadOverheadData', params: { uid }, status: overheadData ? 'fulfilled' : 'rejected', error: null });
      const orderWarnings = ordersDiagnostics?.warnings || [];

      /* ------------------------- build metrics ------------------------- */
      const metrics = {};

      // Approved orders in the window, bucketed by signing month (createdAt).
      const inWindow = (iso) => {
        if (!iso) return false;
        const d = new Date(iso);
        return d >= tf.start && d < tf.end;
      };
      const windowOrders = (orders || []).filter((o) => isApprovedOrder(o) && o.price > 0 && inWindow(o.createdAt));
      // Margin math only uses orders that actually carry a cost — a missing
      // cost would otherwise read as a fake 100% margin.
      const costedOrders = windowOrders.filter((o) => o.cost != null && o.cost > 0);
      const uncostedCount = windowOrders.length - costedOrders.length;

      const bucketed = tf.months.map(({ label }) => ({ label, gpm: null, revenue: null, grossProfit: null }));
      let sumPrice = 0;
      let sumCost = 0;
      const monthAgg = tf.months.map(() => ({ price: 0, cost: 0, any: false }));
      costedOrders.forEach((o) => {
        const d = new Date(o.createdAt);
        const i = monthPos.get(`${d.getFullYear()}-${d.getMonth()}`);
        if (i == null) return;
        monthAgg[i].price += o.price;
        monthAgg[i].cost += o.cost;
        monthAgg[i].any = true;
        sumPrice += o.price;
        sumCost += o.cost;
      });
      monthAgg.forEach((agg, i) => {
        if (!agg.any || agg.price <= 0) return;
        const gp = agg.price - agg.cost;
        bucketed[i].gpm = Math.round(((gp / agg.price) * 100) * 10) / 10;
        bucketed[i].revenue = agg.price;
        bucketed[i].grossProfit = gp;
      });

      const windowGrossProfit = sumPrice > 0 ? sumPrice - sumCost : null;
      const windowGpm = sumPrice > 0 ? ((sumPrice - sumCost) / sumPrice) * 100 : null;
      const monthsWithData = monthAgg.filter((a) => a.any).length;

      // Overhead over the window = monthly overhead × elapsed months (ytd in
      // July counts 7 months, not 12).
      const elapsedMonths = tf.months.filter(({ year, monthIndex }) => new Date(year, monthIndex, 1) <= today).length;
      const windowOverhead = monthlyOverhead != null ? monthlyOverhead * elapsedMonths : null;
      const windowNetProfit = windowGrossProfit != null && windowOverhead != null ? windowGrossProfit - windowOverhead : null;
      const windowNetPct = windowNetProfit != null && sumPrice > 0 ? (windowNetProfit / sumPrice) * 100 : null;

      // GROSS PROFIT MARGIN (hero — monthly trend from signed work)
      if (windowGpm != null) {
        const status = getMetricStatus('gpm', windowGpm);
        metrics.gpm = {
          value: fmtPercent(windowGpm), rawValue: windowGpm, status, source: 'jobtread',
          series: bucketed,
          sub: `Gross profit ${fmtCurrency(windowGrossProfit)} on ${fmtCurrency(sumPrice)} signed · ${tfLabel}`,
          cue: getCoachingCue('gpm', status),
          trace: makeTrace('gpm', {
            source: 'jobtread',
            sourceReason: `Approved customer orders with price + cost over ${tfLabel} (${costedOrders.length} orders, ${monthsWithData} month${monthsWithData === 1 ? '' : 's'} with data)`,
            formula: `GPM = (Σ price ${fmtN(sumPrice)} − Σ cost ${fmtN(sumCost)}) / Σ price × 100 = ${fmtPercent(windowGpm)}`,
            inputs: { sumPrice: Math.round(sumPrice), sumCost: Math.round(sumCost), costedOrders: costedOrders.length, uncostedOrders: uncostedCount },
            warnings: [
              ...(uncostedCount > 0 ? [`${uncostedCount} signed order${uncostedCount === 1 ? '' : 's'} in the window carry no cost and are excluded from margin math.`] : []),
              ...orderWarnings
            ]
          })
        };
      } else {
        metrics.gpm = {
          ...EMPTY_METRIC,
          cue: 'Add costs to your JobTread estimates to chart your margins.',
          trace: makeTrace('gpm', {
            source: 'none',
            sourceReason: jobtreadConnected
              ? (windowOrders.length > 0 ? 'Signed orders in the window carry no costs — margin cannot be computed' : 'No signed customer orders in this window')
              : 'JobTread not connected',
            inputs: { windowOrders: windowOrders.length, uncostedOrders: uncostedCount },
            warnings: orderWarnings
          })
        };
      }

      // GROSS PROFIT ($)
      if (windowGrossProfit != null) {
        metrics.grossProfit = {
          value: fmtCurrency(windowGrossProfit), rawValue: windowGrossProfit, status: 'none', source: 'jobtread', series: [],
          sub: 'Signed price minus estimated costs',
          cue: windowGrossProfit < 0 ? 'Estimated costs exceed signed prices over this window.' : 'What your signed work earns before overhead.',
          trace: makeTrace('grossProfit', {
            source: 'jobtread',
            sourceReason: 'Approved customer orders: Σ(price − cost) over the window',
            formula: `Gross profit = ${fmtN(sumPrice)} − ${fmtN(sumCost)} = ${fmtCurrency(windowGrossProfit)}`,
            inputs: { sumPrice: Math.round(sumPrice), sumCost: Math.round(sumCost), costedOrders: costedOrders.length },
            warnings: orderWarnings
          })
        };
      } else {
        metrics.grossProfit = { ...EMPTY_METRIC, cue: 'Add costs to your JobTread estimates for gross profit.', trace: makeTrace('grossProfit', { source: 'none', sourceReason: 'No costed signed orders in window', warnings: orderWarnings }) };
      }

      // NET MARGIN (% headline, $ in sub — gross profit minus overhead)
      if (windowNetPct != null) {
        const status = getMetricStatus('npm', windowNetPct);
        metrics.netMargin = {
          value: fmtPercent(windowNetPct), rawValue: windowNetPct, status, source: 'jobtread',
          series: [],
          sub: `${fmtCurrency(windowNetProfit)} after ${fmtCurrency(windowOverhead)} overhead`,
          cue: getCoachingCue('npm', status),
          trace: makeTrace('netMargin', {
            source: 'jobtread',
            sourceReason: `JobTread gross profit minus Overhead Calculator overhead (${fmtCurrency(monthlyOverhead)}/mo × ${elapsedMonths} months)`,
            formula: `Net = (GP ${fmtN(windowGrossProfit)} − overhead ${fmtN(windowOverhead)}) / revenue ${fmtN(sumPrice)} × 100 = ${fmtPercent(windowNetPct)}`,
            inputs: { windowGrossProfit: Math.round(windowGrossProfit), windowOverhead: Math.round(windowOverhead), windowNetProfit: Math.round(windowNetProfit), monthlyOverhead: Math.round(monthlyOverhead), elapsedMonths },
            warnings: orderWarnings
          })
        };
      } else {
        const missingOverhead = monthlyOverhead == null;
        metrics.netMargin = {
          ...EMPTY_METRIC,
          cue: missingOverhead ? 'Fill out your Overhead Calculator to unlock net margin.' : 'Net margin appears once signed orders carry costs.',
          trace: makeTrace('netMargin', {
            source: 'none',
            sourceReason: missingOverhead
              ? 'No overhead saved in the Overhead Calculator — net margin needs it'
              : 'No costed signed orders in the window',
            inputs: { overheadItemCount, monthlyOverhead },
            warnings: orderWarnings
          })
        };
      }

      // NET PROFIT ($ headline — same math as net margin, dollar-first)
      if (windowNetProfit != null && sumPrice > 0) {
        const status = getMetricStatus('npm', windowNetPct);
        metrics.netProfit = {
          value: fmtCurrency(windowNetProfit), rawValue: windowNetProfit, status, source: 'jobtread', series: [],
          sub: `${fmtPercent(windowNetPct)} net margin · after ${fmtCurrency(windowOverhead)} overhead`,
          cue: windowNetProfit < 0 ? 'Overhead is outrunning gross profit this window.' : 'Take-home after job costs and overhead.',
          trace: makeTrace('netProfit', {
            source: 'jobtread',
            sourceReason: `JobTread gross profit minus Overhead Calculator overhead (${fmtCurrency(monthlyOverhead)}/mo × ${elapsedMonths} months)`,
            formula: `Net profit = GP ${fmtN(windowGrossProfit)} − overhead ${fmtN(windowOverhead)} = ${fmtCurrency(windowNetProfit)}`,
            inputs: { windowGrossProfit: Math.round(windowGrossProfit), windowOverhead: Math.round(windowOverhead), windowNetProfit: Math.round(windowNetProfit), elapsedMonths },
            warnings: orderWarnings
          })
        };
      } else {
        const missingOverhead = monthlyOverhead == null;
        metrics.netProfit = {
          ...EMPTY_METRIC,
          cue: missingOverhead ? 'Fill out your Overhead Calculator to unlock net profit.' : 'Net profit appears once signed orders carry costs.',
          trace: makeTrace('netProfit', {
            source: 'none',
            sourceReason: missingOverhead ? 'No overhead saved in the Overhead Calculator' : 'No costed signed orders in the window',
            inputs: { overheadItemCount, monthlyOverhead },
            warnings: orderWarnings
          })
        };
      }

      // ACCOUNTS RECEIVABLE ($, snapshot)
      if (receivables && receivables.count > 0) {
        metrics.receivables = {
          value: fmtCurrency(receivables.total), rawValue: receivables.total, status: 'none', source: 'jobtread', series: [],
          sub: `${receivables.count} outstanding customer invoice${receivables.count === 1 ? '' : 's'} · right now`,
          cue: 'Cash you’ve earned that hasn’t landed yet.',
          trace: makeTrace('receivables', {
            source: 'jobtread',
            sourceReason: 'Pending customer invoices with a price, summed',
            formula: `A/R = Σ pending invoice prices = ${fmtCurrency(receivables.total)}`,
            inputs: { total: receivables.total, count: receivables.count },
            warnings: receivables.diagnostics?.warnings || []
          })
        };
      } else {
        metrics.receivables = { ...EMPTY_METRIC, cue: 'No outstanding customer invoices right now.', trace: makeTrace('receivables', { source: 'none', sourceReason: receivables ? 'No pending invoices found' : 'JobTread receivables unavailable', warnings: receivables?.diagnostics?.warnings || [] }) };
      }

      // ACCOUNTS PAYABLE ($, snapshot)
      if (payables && payables.count > 0) {
        metrics.payables = {
          value: fmtCurrency(payables.total), rawValue: payables.total, status: 'none', source: 'jobtread', series: [],
          sub: `${payables.count} unpaid vendor bill${payables.count === 1 ? '' : 's'} · right now`,
          cue: 'What you owe vendors and subs.',
          trace: makeTrace('payables', {
            source: 'jobtread',
            sourceReason: `Pending vendor bills (type: ${payables.diagnostics?.typeUsed || 'unknown'}), summed`,
            formula: `A/P = Σ pending bill prices = ${fmtCurrency(payables.total)}`,
            inputs: { total: payables.total, count: payables.count },
            warnings: payables.diagnostics?.warnings || []
          })
        };
      } else {
        metrics.payables = { ...EMPTY_METRIC, cue: 'No unpaid vendor bills right now.', trace: makeTrace('payables', { source: 'none', sourceReason: payables ? 'No pending vendor bills found' : 'JobTread payables unavailable', warnings: payables?.diagnostics?.warnings || [] }) };
      }

      // OVERHEAD (from the Overhead Calculator — feeds net margin)
      if (monthlyOverhead != null) {
        metrics.overhead = {
          value: `${fmtCurrency(monthlyOverhead)}/mo`, rawValue: monthlyOverhead, monthly: monthlyOverhead, annual: annualOverhead,
          mode: overheadMode, status: 'none', source: 'manual', series: [],
          sub: overheadMode === 'quick' ? 'Quick total · from your Overhead Calculator' : 'From your Overhead Calculator',
          cue: 'Feeds the net margin math above.',
          trace: makeTrace('overhead', {
            source: 'manual',
            sourceReason: overheadMode === 'quick'
              ? 'Quick overhead — a single fixed total typed in the calculator'
              : `Overhead Calculator expenses annualized (${overheadItemCount} items)`,
            formula: `Monthly overhead = annual ${fmtN(annualOverhead)} / 12 = ${fmtCurrency(monthlyOverhead)}`,
            inputs: { annualOverhead: Math.round(annualOverhead), overheadItemCount, mode: overheadMode }
          })
        };
      } else {
        metrics.overhead = {
          ...EMPTY_METRIC, monthly: null, annual: null, mode: overheadMode,
          cue: 'Enter your overhead to unlock net margin.',
          trace: makeTrace('overhead', { source: 'none', sourceReason: 'No overhead saved in the Overhead Calculator', inputs: { overheadItemCount, mode: overheadMode } })
        };
      }

      const debug = {
        uid,
        timeframe,
        window: { start: tf.start.toISOString().split('T')[0], end: tf.end.toISOString().split('T')[0] },
        fetches,
        connections: {
          jobtread: {
            connected: jobtreadConnected,
            orgId: jtOrg.orgId,
            orgName: jtOrg.orgName,
            orgCount: jtOrg.orgCount,
            multipleOrgs: jtOrg.multipleOrgs,
            ordersDiagnostics,
            receivablesDiagnostics: receivables?.diagnostics || null,
            payablesDiagnostics: payables?.diagnostics || null
          },
          overhead: {
            hasData: monthlyOverhead != null,
            monthlyOverhead: monthlyOverhead != null ? Math.round(monthlyOverhead) : null,
            annualOverhead: annualOverhead != null ? Math.round(annualOverhead) : null,
            itemCount: overheadItemCount
          }
        }
      };

      if (cancelled) return;
      setState({ jobtreadConnected, jobtreadError: jtError, asOfDate: today, debug, metrics });
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [currentUser?.uid, userId, reloadIndex, timeframe]);

  return { loading, refresh, ...state };
};

export default useFinanceAnalytics;
